import { useState } from 'react';
import { X, Lock, Loader2 } from 'lucide-react';
import api from '@/lib/axios';
import { useApiError } from '@/hooks/useApiError';

interface AdminAuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (adminToken: string) => void;
  actionName?: string;
}

export function AdminAuthModal({ isOpen, onClose, onSuccess, actionName = 'esta acción' }: AdminAuthModalProps) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { handleApiError } = useApiError();

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username || !password) return;
    setLoading(true);
    try {
      // Intentamos hacer login por debajo del agua sin afectar el store
      const res = await api.post('/auth/login', { username, password });
      const { user, accessToken } = res.data;
      
      const role = user?.role || '';
      if (!['SUPER_ADMIN', 'ADMIN', 'GERENTE'].includes(role)) {
        alert('Credenciales incorrectas o el usuario no tiene permisos de administrador.');
      } else {
        onSuccess(accessToken);
      }
    } catch (err: any) {
      if (err.response?.status === 401) {
        alert('Usuario o contraseña incorrectos.');
      } else {
        handleApiError(err);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-gray-100 bg-gray-50">
          <h2 className="text-xl font-black text-gray-800 flex items-center gap-2">
            <Lock className="text-[#FF3B30]" />
            Autorización
          </h2>
          <button onClick={onClose} className="p-2 text-gray-400 hover:bg-gray-200 rounded-full transition-colors">
            <X size={20} />
          </button>
        </div>

        <div className="p-6">
          <p className="text-sm text-gray-600 mb-6 text-center">
            Se requieren permisos de administrador para realizar <span className="font-bold">{actionName}</span>.
          </p>
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div>
              <label className="block text-xs font-bold text-gray-500 mb-1">USUARIO</label>
              <input 
                type="text"
                autoFocus
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="ios-input w-full"
                placeholder="Usuario administrador"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-500 mb-1">CONTRASEÑA</label>
              <input 
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="ios-input w-full"
                placeholder="Contraseña"
              />
            </div>
            <button 
              type="submit"
              disabled={loading || !username || !password}
              className="mt-2 w-full py-3 bg-[#FF3B30] text-white rounded-xl font-bold uppercase tracking-wider hover:bg-red-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {loading ? <Loader2 className="animate-spin" /> : 'Autorizar'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
